// Validador del CMS — chequea que un valor matchea un FieldDef o SectionDef.
// Usado por el admin React (UX) y por el endpoint que recibe el save (defensa).

import type {
  CmsSchema,
  FieldDef,
  SectionDef,
  ValidationError,
  ValidationResult,
} from './types.ts';

/** Valida un valor contra un FieldDef. */
export function validateField(
  field: FieldDef,
  value: unknown,
  path: string,
): ValidationError[] {
  const errors: ValidationError[] = [];

  // Required check
  const isEmpty =
    value === undefined ||
    value === null ||
    (typeof value === 'string' && value.length === 0) ||
    (Array.isArray(value) && value.length === 0);

  if (field.required && isEmpty) {
    errors.push({ path, message: `${field.label || path} es obligatorio` });
    return errors;
  }

  // Si está vacío y no es required, OK.
  if (isEmpty) return errors;

  switch (field.type) {
    case 'string':
    case 'text':
    case 'richtext':
    case 'markdown':
    case 'url':
    case 'email':
    case 'icon':
    case 'color': {
      if (typeof value !== 'string') {
        errors.push({ path, message: `debe ser texto`, value });
        break;
      }
      // Largo recomendado (max/min) → ADVERTENCIA, no bloquea el guardado.
      // El largo es una guía (ej. SEO); el editor avisa con un contador pero
      // puede guardar igual. Lo que sí bloquea: required, tipo y formato.
      if ('max' in field && field.max !== undefined && value.length > field.max) {
        errors.push({
          path,
          message: `máximo recomendado ${field.max} caracteres (tiene ${value.length})`,
          value,
          severity: 'warning',
        });
      }
      if ('min' in field && field.min !== undefined && value.length < field.min) {
        errors.push({
          path,
          message: `mínimo recomendado ${field.min} caracteres (tiene ${value.length})`,
          value,
          severity: 'warning',
        });
      }
      if (field.type === 'string' && field.pattern) {
        try {
          if (!new RegExp(field.pattern).test(value)) {
            errors.push({
              path,
              message: `no coincide con el formato esperado`,
              value,
            });
          }
        } catch {
          // pattern inválido en el schema — error de definición, no de contenido
        }
      }
      if (field.type === 'url') {
        // Acepta:
        //   - URLs absolutas: https://example.com/...
        //   - Paths internos del sitio: /sistema-focus, /planes, /#anchor, /?q=1
        //   - Mailto / tel / etc: mailto:foo@bar, tel:+123
        const isRelative = value.startsWith('/') || value.startsWith('#');
        const isProtocol = /^[a-z][a-z0-9+.-]*:/i.test(value);
        if (!isRelative) {
          if (!isProtocol) {
            errors.push({
              path,
              message: `URL inválida (debe empezar con / o con un protocolo)`,
              value,
            });
          } else {
            try {
              const u = new URL(value);
              if (field.https_only && u.protocol !== 'https:') {
                errors.push({ path, message: `debe ser una URL HTTPS`, value });
              }
            } catch {
              errors.push({ path, message: `URL inválida`, value });
            }
          }
        }
      }
      if (field.type === 'email') {
        // Validación liviana — el server hace la real
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
          errors.push({ path, message: `email inválido`, value });
        }
      }
      if (field.type === 'color') {
        if (!/^#([0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i.test(value)) {
          errors.push({
            path,
            message: `debe ser un color hex (ej: #ff8800)`,
            value,
          });
        }
      }
      break;
    }

    case 'number': {
      if (typeof value !== 'number' || Number.isNaN(value)) {
        errors.push({ path, message: `debe ser número`, value });
        break;
      }
      if (field.integer && !Number.isInteger(value)) {
        errors.push({ path, message: `debe ser entero`, value });
      }
      if (field.min !== undefined && value < field.min) {
        errors.push({ path, message: `mínimo ${field.min}`, value });
      }
      if (field.max !== undefined && value > field.max) {
        errors.push({ path, message: `máximo ${field.max}`, value });
      }
      break;
    }

    case 'boolean': {
      if (typeof value !== 'boolean') {
        errors.push({ path, message: `debe ser true/false`, value });
      }
      break;
    }

    case 'date': {
      if (typeof value !== 'string' || Number.isNaN(Date.parse(value))) {
        errors.push({ path, message: `fecha inválida`, value });
      }
      break;
    }

    case 'image': {
      // Image guardada como objeto { src, alt, width?, height? }.
      if (typeof value !== 'object' || Array.isArray(value) || value === null) {
        errors.push({ path, message: `imagen inválida (debe ser objeto)`, value });
        break;
      }
      const img = value as Record<string, unknown>;
      const srcEmpty = typeof img.src !== 'string' || img.src.length === 0;
      // Si el field no es required y la imagen está "vacía" (sin src), tratarla
      // como ausente — caso típico: ogImage opcional que el cliente no subió.
      if (srcEmpty && !field.required) {
        break;
      }
      if (srcEmpty) {
        errors.push({ path: `${path}.src`, message: `falta la ruta de la imagen` });
      }
      if (typeof img.alt !== 'string' || img.alt.length === 0) {
        if (!srcEmpty) {
          // Solo exigimos alt si hay src real (sino es un objeto vacío opcional).
          errors.push({ path: `${path}.alt`, message: `el alt es obligatorio` });
        }
      }
      if (
        img.width !== undefined &&
        img.width !== null &&
        typeof img.width !== 'number'
      ) {
        errors.push({ path: `${path}.width`, message: `width debe ser número o null` });
      }
      if (
        img.height !== undefined &&
        img.height !== null &&
        typeof img.height !== 'number'
      ) {
        errors.push({ path: `${path}.height`, message: `height debe ser número o null` });
      }
      break;
    }

    case 'file': {
      // File: { src, name?, mime?, size? }. Dual-read: string URL legacy (Certificat file_url).
      if (typeof value === 'string') {
        if (value.length === 0 && !field.required) break;
        if (value.length === 0) {
          errors.push({ path, message: `falta el archivo`, value });
        }
        break;
      }
      if (typeof value !== 'object' || Array.isArray(value) || value === null) {
        errors.push({ path, message: `archivo inválido (debe ser objeto)`, value });
        break;
      }
      const f = value as Record<string, unknown>;
      const srcEmpty = typeof f.src !== 'string' || f.src.length === 0;
      if (srcEmpty && !field.required) break;
      if (srcEmpty) {
        errors.push({ path: `${path}.src`, message: `falta la ruta del archivo` });
      }
      if (
        f.size !== undefined &&
        f.size !== null &&
        typeof f.size !== 'number'
      ) {
        errors.push({ path: `${path}.size`, message: `size debe ser número o null` });
      }
      break;
    }

    case 'enum': {
      if (typeof value !== 'string' || !field.options.includes(value)) {
        errors.push({
          path,
          message: `debe ser uno de: ${field.options.join(', ')}`,
          value,
        });
      }
      break;
    }

    case 'ref': {
      // El valor es el `valueKey` (slug) de la sección referenciada. Las opciones
      // son dinámicas (vienen de otra sección) → solo validamos que sea string.
      if (typeof value !== 'string') {
        errors.push({ path, message: `debe ser un texto (referencia)`, value });
      }
      break;
    }

    case 'object': {
      if (typeof value !== 'object' || Array.isArray(value) || value === null) {
        errors.push({ path, message: `debe ser un objeto`, value });
        break;
      }
      const obj = value as Record<string, unknown>;
      for (const [subKey, subField] of Object.entries(field.fields)) {
        errors.push(
          ...validateField(subField, obj[subKey], `${path}.${subKey}`),
        );
      }
      break;
    }

    case 'list': {
      if (!Array.isArray(value)) {
        errors.push({ path, message: `debe ser una lista`, value });
        break;
      }
      if (field.min_items !== undefined && value.length < field.min_items) {
        errors.push({
          path,
          message: `mínimo ${field.min_items} items`,
          value: value.length,
        });
      }
      if (field.max_items !== undefined && value.length > field.max_items) {
        errors.push({
          path,
          message: `máximo ${field.max_items} items`,
          value: value.length,
        });
      }
      value.forEach((item, idx) => {
        if (typeof item !== 'object' || item === null) {
          errors.push({
            path: `${path}[${idx}]`,
            message: `cada item debe ser un objeto`,
          });
          return;
        }
        const itemObj = item as Record<string, unknown>;
        for (const [k, def] of Object.entries(field.item_schema)) {
          errors.push(
            ...validateField(def, itemObj[k], `${path}[${idx}].${k}`),
          );
        }
      });
      break;
    }

    case 'union': {
      if (typeof value !== 'object' || Array.isArray(value) || value === null) {
        errors.push({ path, message: `debe ser un objeto`, value });
        break;
      }
      const obj = value as Record<string, unknown>;
      const discKey = field.discriminant ?? 'tipo';
      const variantKey = obj[discKey];
      if (typeof variantKey !== 'string') {
        errors.push({
          path: `${path}.${discKey}`,
          message: `falta el discriminador "${discKey}"`,
        });
        break;
      }
      const variant = field.variants[variantKey];
      if (!variant) {
        errors.push({
          path: `${path}.${discKey}`,
          message: `variante "${variantKey}" no es válida (opciones: ${Object.keys(field.variants).join(', ')})`,
          value: variantKey,
        });
        break;
      }
      for (const [subKey, subField] of Object.entries(variant.fields)) {
        errors.push(
          ...validateField(subField, obj[subKey], `${path}.${subKey}`),
        );
      }
      break;
    }
  }

  return errors;
}

/** Valida una sección (object o list) contra su SectionDef. */
export function validateSection(
  section: SectionDef,
  data: unknown,
  path: string,
): ValidationError[] {
  const errors: ValidationError[] = [];

  if (section.type === 'object') {
    if (typeof data !== 'object' || Array.isArray(data) || data === null) {
      errors.push({ path, message: `la sección debe ser un objeto` });
      return errors;
    }
    const obj = data as Record<string, unknown>;
    for (const [k, fieldDef] of Object.entries(section.fields ?? {})) {
      errors.push(...validateField(fieldDef, obj[k], `${path}.${k}`));
    }
  } else if (section.type === 'list') {
    if (!Array.isArray(data)) {
      errors.push({ path, message: `la sección debe ser una lista` });
      return errors;
    }
    if (section.min_items !== undefined && data.length < section.min_items) {
      errors.push({
        path,
        message: `mínimo ${section.min_items} items`,
        value: data.length,
      });
    }
    if (section.max_items !== undefined && data.length > section.max_items) {
      errors.push({
        path,
        message: `máximo ${section.max_items} items`,
        value: data.length,
      });
    }
    data.forEach((item, idx) => {
      if (typeof item !== 'object' || item === null) {
        errors.push({
          path: `${path}[${idx}]`,
          message: `cada item debe ser un objeto`,
        });
        return;
      }
      const itemObj = item as Record<string, unknown>;
      for (const [k, def] of Object.entries(section.item_schema ?? {})) {
        errors.push(...validateField(def, itemObj[k], `${path}[${idx}].${k}`));
      }
    });
  }

  return errors;
}

/** Valida contenido completo (todas las secciones) contra un CmsSchema. */
export function validateContent(
  schema: CmsSchema,
  content: Record<string, unknown>,
): ValidationResult {
  const errors: ValidationError[] = [];
  for (const [sectionKey, sectionDef] of Object.entries(schema.sections)) {
    if (!(sectionKey in content)) {
      // Sección ausente — OK si el cliente no la editó todavía.
      continue;
    }
    errors.push(
      ...validateSection(sectionDef, content[sectionKey], sectionKey),
    );
  }
  return { ok: errors.length === 0, errors };
}

/** Genera valores default para un schema completo (útil al crear contenido inicial). */
export function defaultsForSchema(schema: CmsSchema): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [key, section] of Object.entries(schema.sections)) {
    out[key] = defaultsForSection(section);
  }
  return out;
}

function defaultsForSection(section: SectionDef): unknown {
  if (section.type === 'object') {
    const obj: Record<string, unknown> = {};
    for (const [k, def] of Object.entries(section.fields ?? {})) {
      obj[k] = defaultForField(def);
    }
    return obj;
  }
  return [];
}

function defaultForField(field: FieldDef): unknown {
  if (field.default !== undefined) return field.default;
  switch (field.type) {
    case 'string':
    case 'text':
    case 'richtext':
    case 'markdown':
    case 'url':
    case 'email':
    case 'date':
    case 'color':
      return '';
    case 'image':
      return { src: '', alt: '', width: null, height: null };
    case 'file':
      return { src: '', name: '', mime: '', size: null };
    case 'number':
      return 0;
    case 'boolean':
      return false;
    case 'enum':
      return field.options[0] ?? '';
    case 'ref':
      return '';
    case 'icon':
      return field.default ?? '';
    case 'object': {
      const obj: Record<string, unknown> = {};
      for (const [k, def] of Object.entries(field.fields)) {
        obj[k] = defaultForField(def);
      }
      return obj;
    }
    case 'list':
      return [];
    case 'union': {
      const discKey = field.discriminant ?? 'tipo';
      const firstVariantKey = Object.keys(field.variants)[0];
      if (!firstVariantKey) return { [discKey]: '' };
      const variant = field.variants[firstVariantKey]!;
      const obj: Record<string, unknown> = { [discKey]: firstVariantKey };
      for (const [k, def] of Object.entries(variant.fields)) {
        obj[k] = defaultForField(def);
      }
      return obj;
    }
  }
}
